import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { provideHttpClient } from '@angular/common/http';
import { DEFAULT_CURRENCY_CODE, ErrorHandler } from '@angular/core';
import {
    ApplicationConfig,
    inject,
    isDevMode,
    provideAppInitializer,
} from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideServiceWorker } from '@angular/service-worker';
import { NoPreloading, provideRouter, withInMemoryScrolling, withNavigationErrorHandler, withPreloading } from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { PermissionPreloadingStrategy } from 'app/core/permission';
import { PwaInstallService } from 'app/core/pwa/pwa-install.service';
import { ChunkLoadRecoveryErrorHandler } from 'app/core/pwa/chunk-load-recovery.error-handler';
import { isLazyChunkLoadError, recoverFromChunkLoadError } from 'app/core/pwa/chunk-load-recovery';
import { PwaUpdateService } from 'app/core/pwa/pwa-update.service';
import { ThemePreferenceService } from 'app/core/services/theme-preference.service';
import { readStoredScheme } from 'app/core/theme/scheme-preference';
import { COMPANY_DATE_PIPE_DEFAULTS, COMPANY_MAT_DATE_FORMATS } from 'app/core/services/company-date-format';
import { CompanyDateFormatService } from 'app/core/services/company-date-format.service';
import { AuthService } from 'app/core/auth/auth.service';
import { useAngularNgsw } from 'app/core/pwa/pwa-env';
import { MockApiService } from 'app/mock-api';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { provideToastr } from 'ngx-toastr';
import { provideSweetAlert2 } from "@sweetalert2/ngx-sweetalert2";


export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideToastr({
            positionClass: 'toast-top-center',
        }),
        provideSweetAlert2(),
        provideHttpClient(),
        { provide: DEFAULT_CURRENCY_CODE, useValue: 'PKR' },
        { provide: ErrorHandler, useClass: ChunkLoadRecoveryErrorHandler },
        provideServiceWorker('ngsw-worker.js', {
            enabled: useAngularNgsw(),
            registrationStrategy: 'registerImmediately',
        }),
        provideRouter(
            appRoutes,
            withNavigationErrorHandler((error) => {
                if (isLazyChunkLoadError(error.error ?? error)) {
                    void recoverFromChunkLoadError();
                    return;
                }
                throw error;
            }),
            withPreloading(isDevMode() ? NoPreloading : PermissionPreloadingStrategy),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: COMPANY_MAT_DATE_FORMATS,
        },
        {
            provide: DATE_PIPE_DEFAULT_OPTIONS,
            useValue: COMPANY_DATE_PIPE_DEFAULTS,
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'tr',
                        label: 'Turkish',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const defaultLang = translocoService.getDefaultLang();
            translocoService.setActiveLang(defaultLang);

            return firstValueFrom(translocoService.load(defaultLang));
        }),
        provideAppInitializer(() => {
            inject(PwaInstallService).init();
            inject(PwaUpdateService).init();
        }),
        provideAppInitializer(() => {
            inject(ThemePreferenceService).init();
        }),
        provideAppInitializer(() => {
            const auth = inject(AuthService);
            if (!auth.accessToken) {
                return;
            }
            return firstValueFrom(inject(CompanyDateFormatService).reload());
        }),

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'classy',
                scheme: readStoredScheme() ?? 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-brand',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
